// src/pages/Privacy.jsx
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, MapPin, Globe } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-900">{title}</h3>
    <div className="text-stone-700 leading-relaxed space-y-3">{children}</div>
  </div>
);

export const Privacy = () => {
  const lastUpdated = "February 22, 2026";

  return (
    <div className="bg-white text-stone-900">
      {/* Header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-stone-950 to-black" />
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://images.unsplash.com/photo-1450101215322-bf5cd27642fc?auto=format&fit=crop&w=1800&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm text-white/90">
              <ShieldCheck className="w-4 h-4" />
              <span>Privacy & Data Protection</span>
            </div>

            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mt-6 leading-[1.05]">
              Privacy Policy
            </h1>

            <p className="text-white/85 text-base md:text-lg mt-4 max-w-2xl">
              GoldenLife Global Impact Foundation (GLGIF) is committed to protecting your privacy while we deliver
              humanitarian support, empowerment programs, and community impact.
            </p>

            <p className="text-white/70 text-sm mt-4">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Main */}
            <div className="lg:col-span-8 space-y-10">
              <Card className="rounded-2xl border-stone-200/70 shadow-sm">
                <CardContent className="p-7 md:p-8 space-y-10">
                  <Section title="1. Who We Are">
                    <p>
                      This Privacy Policy explains how GLGIF collects, uses, discloses, and protects information when you
                      visit our website, volunteer, partner with us, donate, subscribe to our newsletter, or contact us.
                    </p>
                    <p>
                      GLGIF exists to restore dignity, protect the vulnerable, and create long-term opportunity through
                      humanitarian action and community empowerment.
                    </p>
                  </Section>

                  <Section title="2. Information We Collect">
                    <p>We may collect:</p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <b>Contact details</b> (name, email, phone) when you contact us, volunteer, partner, or subscribe.
                      </li>
                      <li>
                        <b>Donation details</b> (amount, reference, basic payer details). We do not store full card/bank
                        credentials on our servers if payments are processed by third-party providers.
                      </li>
                      <li>
                        <b>Content you submit</b> (messages, form responses, story tips, or feedback).
                      </li>
                      <li>
                        <b>Technical data</b> (device/browser data, IP address, pages visited) for security and analytics.
                      </li>
                    </ul>
                  </Section>

                  <Section title="3. How We Use Your Information">
                    <ul className="list-disc pl-5 space-y-2">
                      <li>To respond to inquiries and support requests.</li>
                      <li>To process donations and issue confirmations/receipts when applicable.</li>
                      <li>To manage volunteer and partner engagement.</li>
                      <li>To send newsletters and campaign updates (you can unsubscribe anytime).</li>
                      <li>To improve website performance, security, and user experience.</li>
                      <li>To keep accurate records of our impact, reporting, and accountability.</li>
                    </ul>
                  </Section>

                  <Section title="4. Newsletter Subscriptions">
                    <p>
                      When you submit your email for our newsletter, we store it to send campaign updates and impact
                      stories. If you are already subscribed, we may notify you. You can unsubscribe at any time from any
                      email we send.
                    </p>
                  </Section>

                  <Section title="5. Cookies & Analytics">
                    <p>
                      We may use cookies or similar technologies to keep the site secure, measure usage, and improve
                      content. You can control cookies through your browser settings, but disabling them may affect some
                      features.
                    </p>
                  </Section>

                  <Section title="6. Sharing & Disclosure">
                    <p>We may share information only when necessary, for example:</p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        With <b>service providers</b> (email delivery, hosting, analytics, payment processing) who help us
                        operate.
                      </li>
                      <li>
                        To comply with <b>legal obligations</b> or to protect rights, safety, and security.
                      </li>
                      <li>
                        With your consent, when you request partnership or volunteer coordination.
                      </li>
                    </ul>
                    <p>We do not sell your personal information.</p>
                  </Section>

                  <Section title="7. Data Security">
                    <p>
                      We implement reasonable safeguards to protect your data. However, no method of transmission or
                      storage is 100% secure. If you suspect unauthorized access, contact us immediately.
                    </p>
                  </Section>

                  <Section title="8. Data Retention">
                    <p>
                      We keep personal information only as long as needed for the purposes described in this policy, and
                      for legitimate record-keeping, compliance, and accountability.
                    </p>
                  </Section>

                  <Section title="9. Your Choices & Rights">
                    <p>You may request to:</p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>Access, correct, or update your information.</li>
                      <li>Withdraw newsletter consent or unsubscribe.</li>
                      <li>Request deletion where applicable (subject to legal/record obligations).</li>
                    </ul>
                  </Section>

                  <Section title="10. Children’s Privacy">
                    <p>
                      Our programs support children, but this website is not designed to collect children’s personal data
                      without a parent/guardian or authorized representative. If you believe a child’s data was submitted
                      improperly, contact us.
                    </p>
                  </Section>

                  <Section title="11. Contact Us">
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-700" />
                      <span>Uyo, Akwa Ibom State, Nigeria</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-amber-700" />
                      <span>info@glgif.org</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-amber-700" />
                      <span>We respond as quickly as possible to privacy requests.</span>
                    </p>
                  </Section>
                </CardContent>
              </Card>

              <div className="flex flex-wrap gap-3">
                <Button asChild className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-6">
                  <Link to="/contact">Contact GLGIF</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-2 border-stone-200 text-stone-800 px-6"
                >
                  <Link to="/">Back to Home</Link>
                </Button>
              </div>
            </div>

            {/* Side */}
            <aside className="lg:col-span-4">
              <div className="sticky top-24 space-y-6">
                <Card className="rounded-2xl border-stone-200/70 shadow-sm">
                  <CardContent className="p-6">
                    <p className="font-heading text-lg font-bold text-stone-900 mb-2">Quick Summary</p>
                    <ul className="text-stone-700 text-sm space-y-2 list-disc pl-5">
                      <li>We collect only what we need for impact and communication.</li>
                      <li>We don’t sell your personal data.</li>
                      <li>You can unsubscribe from newsletters anytime.</li>
                      <li>We protect data with reasonable security measures.</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="rounded-2xl border-stone-200/70 shadow-sm">
                  <CardContent className="p-6">
                    <p className="font-heading text-lg font-bold text-stone-900 mb-2">Related</p>
                    <div className="space-y-2">
                      <Link to="/terms" className="text-amber-700 hover:text-amber-800 font-medium">
                        View Terms of Service →
                      </Link>
                      <div className="text-stone-600 text-sm">
                        Learn how to use the website responsibly and what to expect.
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;